import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X, Users, FileText, Tag, Search, Edit3, Trash2, Check, AlertCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Eye, EyeOff, RefreshCw, ChevronDown, ChevronUp, Columns3, Building2, Globe, Loader2, Download, CircleEllipsis, Pencil } from 'lucide-react';
import { Modal } from "@mui/material";


import { showError, showSuccess } from '../utils/toast';
import baseapi from '../services/baseapi';
import { hasPermission } from '../utils/permissionsHelper';
import { jwt_decode } from '../utils/jwt_decode';
import { useClientLevel } from '../hooks/useClientLevel';
import { useNavigate } from 'react-router-dom';
import SingleSelectLocalSearchApiDropdown from '../components/SingleSelectLocalSearchApiDropdown';
import AgentExportModal from './Agent/AgentExportModal';
import { ItemsPerPageDropdown } from '../components/ItemsPerPageDropdown ';

interface Client {
    id: string;
    name: string;
    display_name: string;
    description: string;
    created: string;
    updated: string;
    total_assets: number;
    total_scans: number;
    ticket_company: any;
    ticket_site: any;
}

const Clients = () => {
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [editingClient, setEditingClient] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [clients, setClients] = useState<Client[]>([]);
    const [showExportModal, setShowExportModal] = useState(false);
    const [deleteClient, setDeleteClient] = useState<any>({})
    const [open, setOpen] = useState(false)
    const [isRefreshing, setIsRefreshing] = useState(false);
    //pagination states and vars
    const [totalItems, setTotalItmes] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(5);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    //sorting
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
    // Column visibility state
    const [showColumnFilter, setShowColumnFilter] = useState(false);
    type ColumnKey =
        | 'name'
        | 'display_name'
        | 'description'
        | 'assets'
        | 'scans'
        | 'created'
        | 'updated'
        | 'actions';
    //permissions
    const canCreate = hasPermission('clients', 'create')
    const canUpdate = hasPermission('clients', 'update')
    const canDelete = hasPermission('clients', 'delete')
    const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
        name: true,
        display_name: true,
        description: true,
        assets: true,
        scans: true,
        created: true,
        updated: true,
        actions: canDelete || canUpdate
    });
    const decodedToken = jwt_decode();
    const organizationId = decodedToken?.organization_id;
    const [formData, setFormData] = useState({
        name: '',
        display_name: '',
        description: '',
        ticketing_company: null as any,
        ticketing_site: null as any,
    });
    const [isDeskproEnabled, setIsDeskproEnabled] = useState(false);
    const level = useClientLevel();
    const navigate = useNavigate()
    const isFirstSearch = useRef(true);

    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>, id: string | number) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setDropdownPosition({
            top: rect.bottom + 4,
            left: Math.max(10, rect.left - 110),
        });
        setOpenMenuId(prev => (prev === String(id) ? null : String(id)));
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (openMenuId && !target.closest(`[data-menu-id="${openMenuId}"]`) && !target.closest('[data-action-menu]')) {
                setOpenMenuId(null);
            }
        };
        const handleScroll = () => {
            if (openMenuId) setOpenMenuId(null);
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [openMenuId]);


    useEffect(() => {
        if (level.name !== 'global') {
            navigate('/dashboard')
        }
        else {
            fetchData()
            fetchIntegrations()
        }

    }, []);

    const fetchIntegrations = async () => {
        try {
            const response = await baseapi.get("/integrations")
            if (response.data.status) {
                const deskpro = response.data.data.find((i: any) => i.integration_type === 'deskpro' && i.is_enabled);
                setIsDeskproEnabled(!!deskpro);
            }
        } catch (error) {
            console.error("Error fetching integrations", error);
        }
    }

    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            if (debouncedSearch !== searchTerm) {
                setDebouncedSearch(searchTerm);
                setCurrentPage(1);
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm, debouncedSearch]);

    // Re-fetch when debouncedSearch changes
    useEffect(() => {
        if (isFirstSearch.current) {
            isFirstSearch.current = false;
            return;
        }
        fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
    }, [debouncedSearch]);

    const fetchData = async (skip: number = 0, limit: number = 5, orderBy: Record<string, 'asc' | 'desc'>[] = [{ created: 'desc' }]) => {
        try {
            const [sortEntry] = orderBy;
            const sort_by = Object.keys(sortEntry)[0];
            const sort_order = sortEntry[sort_by];

            setLoading(true)

            const queryObj: any = {};
            if (debouncedSearch) {
                queryObj.$or = [
                    { name: { "$ilike": `%${debouncedSearch}%` } },
                    { display_name: { "$ilike": `%${debouncedSearch}%` } }
                ];
            }

            const params = {
                query: JSON.stringify(queryObj),
                skip: skip,
                limit: limit,
                sort_by: sort_by,
                sort_order: sort_order
            }
            const response = await baseapi.get("/clients", { params })
            if (response.data.status) {
                setClients(response.data.data)
                setTotalPages(Math.ceil(response.data.total / limit))
                setTotalItmes(response.data.total)
                setLoading(false)
            }
            else {
                showError(response.data.message)
                setLoading(false)
            }
        }
        catch (error: any) {
            showError(error.message)
            setLoading(false)
        }
    }

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                const newDirection = prev.direction === 'asc' ? 'desc' : 'asc';
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage, [{ [key]: newDirection }]);
                return { key, direction: newDirection };
            } else {
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage, [{ [key]: 'asc' }]);
                return { key, direction: 'asc' };
            }
        });
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) newErrors.name = 'Client name is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => {
            const newData = { ...prev, [field]: value };
            if (field === 'ticketing_company') {
                newData.ticketing_site = null;
            }
            return newData;
        });
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            const params: any = {
                name: formData.name,
                display_name: formData.display_name,
                description: formData.description,
                organization_id: Number(organizationId),
                status: "ACTIVE"
            }

            if (isDeskproEnabled) {
                params.ticket_company = formData.ticketing_company ? { id: formData.ticketing_company.id, name: formData.ticketing_company.name || formData.ticketing_company.display_name } : null;
                params.ticket_site = formData.ticketing_site ? { id: formData.ticketing_site.id, name: formData.ticketing_site.name || formData.ticketing_site.display_name } : null;
            }
            let response;
            if (editingClient) {
                response = await baseapi.put(`/clients/${editingClient}`, params)
            } else {
                response = await baseapi.post('/clients', params)
            }

            if (response.data.status) {
                showSuccess(response.data.message)
                resetForm();
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage)
                // Trigger Dock refresh
                window.dispatchEvent(new CustomEvent('clients-updated'));
            }
            else {
                showError(response.data.message)
            }
        }
        catch (error: any) {
            showError(error.message)
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            display_name: '',
            description: '',
            ticketing_company: null,
            ticketing_site: null,
        });
        setEditingClient(null);
        setShowAddForm(false);
        setErrors({});
    };

    const handleEdit = (client: Client) => {
        setFormData({
            name: client.name,
            display_name: client.display_name,
            description: client.description,
            ticketing_company: client.ticket_company ? JSON.parse(client.ticket_company) : null,
            ticketing_site: client.ticket_site ? JSON.parse(client.ticket_site) : null,
        });
        setEditingClient(client.id);
        setShowAddForm(true);

    };

    const handleDelete = async (client_id: string) => {
        try {
            const response = await baseapi.delete(`/clients/${client_id}`)
            if (response.data.status) {
                showSuccess("Client deleted successfully")
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage)
                // Trigger Dock refresh
                window.dispatchEvent(new CustomEvent('clients-updated'));
            }
            else {
                showError(response.data.message)
            }
        }
        catch (error: any) {
            showError(error)
        }
    };

    const handleRefresh = () => {
        setIsRefreshing(true);
        setCurrentPage(1);
        setSearchTerm('');
        setDebouncedSearch('');
        fetchData(0, itemsPerPage);
        setTimeout(() => {
            setIsRefreshing(false);
        }, 1000);
    }

    //paginationFunctions
    const handlePageChange = (page: number): void => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            fetchData((itemsPerPage * (page - 1)), itemsPerPage)
        }
    };

    const handleItemsPerPageChange = (value: number): void => {
        setItemsPerPage(value);
        setCurrentPage(1);
        fetchData(0, value)
    };

    // Column visibility functions
    const handleColumnToggle = (column: ColumnKey) => {
        setVisibleColumns(prev => ({
            ...prev,
            [column]: !prev[column]
        }));
    };

    const columnOptions: { key: ColumnKey; label: string }[] = [
        { key: 'name', label: 'Name' },
        { key: 'display_name', label: 'Display Name' },
        { key: 'description', label: 'Description' },
        // { key: 'assets', label: 'Assets' },
        // { key: 'scans', label: 'Scans' },
        { key: 'created', label: 'Created' },
        { key: 'updated', label: 'Updated' },
        ...(canUpdate || canDelete
            ? [{ key: 'actions' as ColumnKey, label: 'Actions' }]
            : []),
    ]

    return (
        <div className="text-gray-800 dark:text-gray-100 p-3 pb-20 min-h-screen transition-colors duration-200">
            <div className="w-full mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-1.5">
                        <div className="bg-orange-600 p-1.5 rounded shadow-lg">
                            <Users className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <h1 className="font-bold text-lg text-gray-900 dark:text-gray-200 leading-none transition-colors duration-200">Clients</h1>
                        </div>
                        {!loading &&
                            <div className='font-bold text-lg text-gray-500 dark:text-gray-100 transition-colors duration-200'>
                                ({totalItems})
                            </div>
                        }
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleRefresh}
                            className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 shadow-sm transition-colors duration-200"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                        </button>

                        <div className="relative w-full sm:w-56">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 w-3.5 h-3.5 transition-colors duration-200" />
                            <input
                                type="text"
                                placeholder="Search clients..."
                                className="w-full bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:border-orange-500 outline-none shadow-sm transition-colors duration-200"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                        </div>

                        <div className="relative" data-col-menu>
                            <button
                                onClick={() => setShowColumnFilter(!showColumnFilter)}
                                className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 shadow-sm transition-colors duration-200 flex items-center gap-2"
                            >
                                <Columns3 className="w-3.5 h-3.5" />
                                <span className="text-[10px] font-bold uppercase tracking-wider">Columns</span>
                            </button>
                            {showColumnFilter && (
                                <div className="absolute top-full mt-2 right-0 w-52 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[110] p-1.5 max-h-[450px] overflow-y-auto transition-colors duration-200">
                                    <div className="px-3 py-1.5 text-[9px] font-bold text-gray-500 dark:text-gray-400 uppercase border-b border-gray-100 dark:border-gray-800 mb-1 transition-colors duration-200">Toggle Columns</div>
                                    {columnOptions.map((option) => (
                                        <button
                                            key={option.key}
                                            onClick={() => handleColumnToggle(option.key)}
                                            className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase text-gray-700 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200"
                                        >
                                            <div className="flex items-center gap-2.5">
                                                {visibleColumns[option.key] ? (
                                                    <Eye className="w-3 h-3 text-orange-500" />
                                                ) : (
                                                    <EyeOff className="w-3 h-3 text-gray-400 dark:text-gray-600" />
                                                )}
                                                {option.label}
                                            </div>
                                            <div className={`w-3 h-3 rounded border flex items-center justify-center transition-colors duration-200 ${visibleColumns[option.key] ? 'bg-orange-600 border-orange-600' : 'border-gray-300 dark:border-gray-700'}`}>
                                                {visibleColumns[option.key] && <Check className="w-2.5 h-2.5 text-white" />}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button
                            onClick={() => setShowExportModal(true)}
                            className="flex items-center gap-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-1.5 text-[11px] text-gray-800 dark:text-white font-bold hover:bg-gray-100 dark:hover:bg-gray-700 shadow-sm transition-colors duration-200"
                        >
                            <Download className="w-4 h-4 text-cyan-500" />
                            Export
                        </button>

                        {canCreate && (
                            <button
                                onClick={() => {
                                    if (showAddForm) {
                                        resetForm();
                                    } else {
                                        setShowAddForm(true);
                                    }
                                }}
                                className="flex items-center gap-1.5 bg-orange-600 rounded-lg px-4 py-1.5 text-[11px] text-white font-bold hover:bg-orange-700 shadow-lg shadow-orange-900/20 transition-colors duration-200"
                            >
                                <Plus className="w-4 h-4" />
                                Add Client
                            </button>
                        )}
                    </div>
                </div>

                    {/* Table */}
                    <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-t-xl overflow-hidden shadow-sm dark:shadow-2xl transition-colors duration-200">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs border-collapse">
                                <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10 transition-colors duration-200">
                                    <tr className="border-b border-gray-200 dark:border-gray-700/50 transition-colors duration-200">
                                        {columnOptions.map(({ key, label }) => (
                                            visibleColumns[key] && (
                                                <th
                                                    key={key}
                                                    onClick={() => key !== 'actions' && handleSort(key)}
                                                    className={`px-1 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest select-none whitespace-nowrap transition-colors duration-200 ${key === 'actions' ? 'text-right' : 'text-left cursor-pointer'}`}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        {label}
                                                        {key !== 'actions' && (
                                                            sortConfig?.key === key ? (
                                                                sortConfig.direction === 'asc' ? (
                                                                    <ChevronUp size={12} />
                                                                ) : (
                                                                    <ChevronDown size={12} />
                                                                )
                                                            ) : (
                                                                <ChevronDown size={12} className="opacity-40" />
                                                            )
                                                        )}
                                                    </div>
                                                </th>
                                            )
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30 transition-colors duration-200">
                                    {clients.length === 0 && !loading ? (
                                        <tr>
                                            <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="py-16 text-center text-gray-400 dark:text-gray-500 font-bold uppercase tracking-widest italic">
                                                No clients found
                                            </td>
                                        </tr>
                                    ) : loading ? (
                                        <tr>
                                            <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="py-16 text-center text-gray-700 dark:text-gray-200 font-bold uppercase tracking-widest italic">
                                                <div className="flex items-center justify-center gap-2">
                                                    <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                                                    Loading clients
                                                </div>
                                            </td>
                                        </tr>
                                    ) :
                                        (
                                            clients.map((client) => (
                                                <tr key={client.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors duration-200 group border-b border-gray-100 dark:border-gray-700/30">
                                                    {visibleColumns.name && (
                                                        <td className="px-1 py-0.5"><span className="text-[11px] text-gray-900 dark:text-gray-200 font-bold font-mono">{client.name}</span></td>
                                                    )}
                                                    {visibleColumns.display_name && (
                                                        <td className="px-1 py-0.5">
                                                            {client.display_name ? (
                                                                <span className="text-[9px] font-mono bg-gray-100 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600/50 text-gray-800 dark:text-gray-200 px-1.5 py-0.5 rounded uppercase transition-colors duration-200">
                                                                    {client.display_name}
                                                                </span>
                                                            ) : (
                                                                <span className="text-gray-400 dark:text-gray-200 font-mono">-</span>
                                                            )}
                                                        </td>
                                                    )}
                                                    {visibleColumns.description && (
                                                        <td className="px-1 py-0.5 text-gray-600 dark:text-gray-400 text-[10px] max-w-xs">
                                                            <div className="truncate" title={client.description}>
                                                                {client.description || '-'}
                                                            </div>
                                                        </td>
                                                    )}
                                                    {visibleColumns.created && (
                                                        <td className="px-1 py-0.5 text-gray-700 dark:text-gray-200 text-[10px]">
                                                            {client.created ? new Date(client.created).toLocaleDateString() : "-"}
                                                        </td>
                                                    )}
                                                    {visibleColumns.updated && (
                                                        <td className="px-1 py-0.5 text-gray-700 dark:text-gray-200 text-[10px]">
                                                            {client.updated ? new Date(client.updated).toLocaleDateString() : "-"}
                                                        </td>
                                                    )}
                                                    {visibleColumns.actions && (
                                                        <td className="px-1 py-0.5">
                                                            <button
                                                                data-menu-id={client.id}
                                                                onClick={(e) => handleMenuOpen(e, client.id)}
                                                                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 cursor-pointer"
                                                            >
                                                                <CircleEllipsis className="w-4.5 h-4.5" />
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))
                                        )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Table Footer */}
                    <div className="bg-gray-50 dark:bg-gray-800/50 border rounded-b-xl border-gray-200 dark:border-gray-700 px-2 py-1 flex items-center justify-between transition-colors duration-200">
                        <div className="flex items-center gap-4">
                            <ItemsPerPageDropdown
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                            />
                            <span className="text-[10px] text-gray-600 dark:text-gray-200 font-bold uppercase tracking-widest transition-colors duration-200">
                                <span className="text-gray-900 dark:text-gray-200">{startIndex + 1}</span> - <span className="text-gray-900 dark:text-gray-200">{Math.min(endIndex, totalItems)}</span> OF {totalItems}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="p-1.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white disabled:opacity-30 transition-colors duration-200"><ChevronsLeft className="w-4 h-4" /></button>
                            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white disabled:opacity-30 transition-colors duration-200"><ChevronLeft className="w-4 h-4" /></button>
                            <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-900/20">{currentPage}</div>
                            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="p-1.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white disabled:opacity-30 transition-colors duration-200"><ChevronRight className="w-4 h-4" /></button>
                            <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className="p-1.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white disabled:opacity-30 transition-colors duration-200"><ChevronsRight className="w-4 h-4" /></button>
                        </div>
                    </div>

                    {/* Sliding Form Panel from Right */}
                    <div
                        className={`fixed top-12 right-0 sm:right-4 h-[calc(100vh-2rem)] w-full max-w-full sm:max-w-[480px] md:max-w-[520px] lg:max-w-[580px] transition-all duration-500 ease-in-out transform z-50 ${showAddForm ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'
                            }`}
                    >
                        <div className="h-min bg-white dark:bg-gradient-to-br dark:from-gray-800 dark:to-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700 rounded-2xl flex flex-col transition-colors duration-200">
                            {/* Form Header */}
                            <div className="flex items-center justify-between px-4 py-3 sm:p-4 border-b border-gray-200 dark:border-gray-700/50 transition-colors duration-200">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 sm:p-3 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl shadow-lg">
                                        {editingClient ? <Edit3 className="text-white" size={20} /> : <Plus className="text-white" size={20} />}
                                    </div>
                                    <div>
                                        <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white transition-colors duration-200">
                                            {editingClient ? 'Edit Client' : 'Add New Client'}
                                        </h2>
                                        <p className="text-gray-500 dark:text-gray-400 text-sm mt-1 transition-colors duration-200">
                                            {editingClient ? 'Update client information' : 'Fill in the details to create a new client'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={resetForm}
                                    className="p-2 sm:p-3 text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-xl transition-colors duration-200"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Form Content */}
                            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                                <div className="space-y-4">
                                    {/* Row 1: Client Name & Display Name */}
                                    <div className="flex flex-col items-center lg:flex-row gap-4">
                                        {/* Client Name */}
                                        <div className="flex-1 w-full">
                                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1 flex items-center gap-2 transition-colors duration-200">
                                                <Users size={15} className="text-orange-500" />
                                                Client Name <span className="text-red-400 text-base">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.name}
                                                onChange={(e) => handleInputChange('name', e.target.value)}
                                                placeholder="Enter client name..."
                                                className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/80 border rounded-lg text-gray-900 dark:text-white placeholder-gray-400 text-sm focus:border-orange-500 outline-none transition-colors duration-200 ${errors.name ? 'border-red-500 bg-red-500/5' : 'border-gray-200 dark:border-gray-600'
                                                    }`}
                                            />
                                            {errors.name && (
                                                <div className="flex items-center gap-2 mt-1 text-red-500 dark:text-red-400 text-sm">
                                                    <AlertCircle size={14} />
                                                    {errors.name}
                                                </div>
                                            )}
                                        </div>

                                        {/* Display Name */}
                                        <div className="flex-1  w-full">
                                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1 flex items-center gap-2 transition-colors duration-200">
                                                <Tag size={15} className="text-orange-500" />
                                                Display Name
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.display_name}
                                                onChange={(e) => handleInputChange('display_name', e.target.value)}
                                                placeholder="Enter display name..."
                                                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 text-sm focus:border-orange-500 outline-none transition-colors duration-200"
                                            />
                                        </div>
                                    </div>

                                    {/* Row 3: Description */}
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1 flex items-center gap-2 transition-colors duration-200">
                                            <FileText size={15} className="text-orange-500" />
                                            Description
                                        </label>
                                        <textarea
                                            value={formData.description}
                                            onChange={(e) => handleInputChange('description', e.target.value)}
                                            placeholder="Enter description..."
                                            rows={3}
                                            className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 text-sm resize-none focus:border-orange-500 outline-none transition-colors duration-200"
                                        />
                                    </div>

                                    {isDeskproEnabled && (
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                            <div>
                                                <SingleSelectLocalSearchApiDropdown
                                                    label="Ticketing Company"
                                                    icon={Building2}
                                                    apiEndpoint="/ticketing/metadata/companies"
                                                    value={formData.ticketing_company}
                                                    onChange={(val) => handleInputChange('ticketing_company', val)}
                                                    placeholder="Select Company"
                                                    searchInputPlaceholder="Search company..."
                                                    displayProperty="name"
                                                    searchFields={['name']}
                                                />
                                            </div>
                                            <div>
                                                <SingleSelectLocalSearchApiDropdown
                                                    label="Ticketing Site"
                                                    icon={Globe}
                                                    apiEndpoint="/ticketing/metadata/sites"
                                                    disabled={!formData.ticketing_company}
                                                    value={formData.ticketing_site}
                                                    onChange={(val) => handleInputChange('ticketing_site', val)}
                                                    placeholder={formData.ticketing_company ? "Select Site" : "Select company first"}
                                                    searchInputPlaceholder="Search site..."
                                                    displayProperty="name"
                                                    searchFields={['name']}
                                                    additionalQueries={{ company_id: formData.ticketing_company?.id }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Form Footer */}
                            <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700/50 transition-colors duration-200">
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <button
                                        onClick={handleSubmit}
                                        disabled={submitting}
                                        className={`w-full sm:flex-1 flex items-center justify-center gap-3 bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-4 rounded-xl font-semibold hover:from-orange-600 hover:to-red-600 transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg text-sm ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {submitting ? (
                                            <RefreshCw className="w-5 h-5 animate-spin" />
                                        ) : (
                                            <Check size={18} />
                                        )}
                                        {submitting ? 'Submitting...' : (editingClient ? 'Update Client' : 'Create Client')}
                                    </button>
                                    <button
                                        onClick={resetForm}
                                        className="w-full sm:w-auto sm:px-6 py-3 bg-gray-100 dark:bg-gray-700/80 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 font-medium text-sm"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Background Overlay */}
                    <div
                        className={`fixed inset-0 bg-black/30 transition-opacity duration-300 z-40 ${showAddForm ? 'opacity-100' : 'opacity-0 pointer-events-none'
                            }`}
                        onClick={resetForm}
                    />
                </div>

            {/* Click outside to close column filter dropdown */}
            {showColumnFilter && (
                <div
                    className="fixed inset-0 z-30"
                    onClick={() => setShowColumnFilter(false)}
                />
            )}
            <AgentExportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                templateName="clients"
                clientId={level?.id}
            />
            <Modal open={open} onClose={() => setOpen(false)}>
                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                w-[95%] sm:w-[500px] md:w-[550px] max-w-2xl bg-white dark:bg-gray-900/95 backdrop-blur-xl 
                border border-red-300 dark:border-red-400/20 shadow-2xl p-4 rounded-2xl text-gray-900 dark:text-white
                relative overflow-hidden transition-colors duration-200">

                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-400/40 to-transparent"></div>

                    <div className="flex items-center mb-4">
                        <div className="flex items-center justify-center mr-2 text-red-500 dark:text-red-400">
                            <Trash2 className='h-7 w-7'></Trash2>
                        </div>
                        <div className='flex items-center justify-center'>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white transition-colors duration-200">Confirm Delete</h2>
                        </div>
                    </div>

                    <div className="mb-4 bg-gray-50 dark:bg-gray-800/30 p-2 rounded-lg border border-gray-200 dark:border-gray-700/50 transition-colors duration-200">
                        <p className="text-gray-800 dark:text-white/90 mb-3 text-lg transition-colors duration-200">
                            Are you sure you want to delete client <span className="font-semibold text-orange-600 dark:text-orange-400">{deleteClient?.name}</span>?
                        </p>
                        <p className="text-orange-600 dark:text-orange-400/70 text-sm transition-colors duration-200">
                            This will permanently remove the client and all associated data from your account.
                        </p>
                    </div>

                    <div className="flex gap-4 justify-end">
                        <button
                            onClick={() => setOpen(false)}
                            className="px-6 py-3 border border-gray-300 dark:border-gray-600/50 text-gray-700 dark:text-gray-300 rounded-lg
                      hover:border-gray-400 hover:bg-gray-100 dark:hover:border-gray-500 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-white
                      transition-all duration-200 font-medium min-w-[100px]"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                handleDelete(deleteClient.id)
                                setOpen(false);
                            }}
                            className="px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold
                      hover:bg-orange-600 hover:shadow-lg hover:shadow-orange-500/20
                      transition-all duration-200 min-w-[100px]"
                        >
                            Delete Client
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Actions Portal Menu */}
            {openMenuId && createPortal(
                (() => {
                    const activeClient = clients.find(c => String(c.id) === String(openMenuId));
                    if (!activeClient) return null;
                    return (
                        <div
                            data-action-menu
                            className="fixed w-40 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[9999] overflow-hidden py-1.5 transition-colors duration-200"
                            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                        >
                            {canUpdate && (
                                <button
                                    onClick={() => {
                                        handleEdit(activeClient);
                                        setOpenMenuId(null);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex text-[11px] items-center gap-3 border-b border-gray-100 dark:border-gray-800 transition-colors duration-200 cursor-pointer"
                                >
                                    <Pencil className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" /> Edit Client
                                </button>
                            )}
                            {canDelete && (
                                <button
                                    onClick={() => {
                                        setDeleteClient(activeClient);
                                        setOpen(true);
                                        setOpenMenuId(null);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-[11px] transition-colors duration-200 flex items-center gap-3 font-bold cursor-pointer"
                                >
                                    <Trash2 className="w-3.5 h-3.5" /> Delete Client
                                </button>
                            )}
                        </div>
                    );
                })(),
                document.body
            )}
        </div>
    );
};
export default Clients;